import { type SubmitEvent, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { sanitizeReturnUrl } from '../auth/returnUrl';

export interface LoginPageProps {
  pageName: string;
  noAccessMessage: ReactNode;
  googleOauth?: boolean;
  banner?: ReactNode;
  inviteToken?: string;
}

export default function LoginPage({
  pageName,
  noAccessMessage,
  googleOauth,
  banner,
  inviteToken,
}: LoginPageProps): ReactElement {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const returnUrl = sanitizeReturnUrl(new URLSearchParams(location.search).get('returnUrl'));
  const noAccess = new URLSearchParams(location.search).get('error') === 'no-access';

  const handleGoogleSignIn = () => {
    const params = new URLSearchParams({ returnUrl });
    if (inviteToken) {
      params.set('inviteToken', inviteToken);
    }
    window.location.assign(`/auth/google/oauth2/init?${params.toString()}`);
  };

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const data = (await response.json().catch(() => null)) as { message?: string } | null;
        throw new Error(data?.message ?? 'Login failed');
      }

      await navigate(returnUrl);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed';
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-sm rounded border bg-white p-6">
        {banner ? (
          <p
            role="status"
            className="mb-4 rounded border-l-4 border-blue-700 bg-blue-100 p-3 text-sm text-blue-950"
          >
            {banner}
          </p>
        ) : null}
        <h1 className="mb-1 text-xl font-semibold text-gray-900">{pageName}</h1>
        <p className="mb-6 text-sm text-gray-600">Sign in to continue</p>
        {noAccess ? (
          <p
            role="status"
            className="mb-4 rounded border-l-4 border-amber-700 bg-amber-100 p-3 text-sm text-amber-950"
          >
            {noAccessMessage}
          </p>
        ) : null}

        <form
          onSubmit={(event) => {
            void handleSubmit(event);
          }}
          className="space-y-4"
        >
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-400"
              placeholder="Enter your username"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-400"
              placeholder="Enter your password"
            />
          </div>

          {errorMessage ? <p className="text-sm text-red-600">{errorMessage}</p> : null}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded bg-gray-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {isSubmitting ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {googleOauth ? (
          <>
            <div className="my-4 flex items-center gap-3">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-xs text-gray-500">or</span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>

            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Sign in With Google
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}
